import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Upload, Loader2, Check, X, Eye, AlertTriangle, Plus } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import Footer from "@/components/Footer";
import PDFPreview from "@/components/PDFPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BASE_DECLARATIONS = [
  "I confirm that the applicant will exit the UAE before visa expiry or complete a valid visa renewal.",
  "I accept full responsibility for any overstay fines, penalties, absconding charges, legal fees, or immigration violations.",
  "I understand that visa approval or rejection is solely at the discretion of UAE Immigration Authorities.",
  "I acknowledge that White Sky Travel and Tourism LLC has no control or influence over visa approvals or rejections.",
  "I confirm that all documents and information provided are genuine, accurate, and valid.",
  "I confirm that the applicant is fully aware of the visa application process, even if applied by a third party.",
  "I declare that there is no other pending or active visa application for the applicant in UAE immigration systems.",
  "I understand that visa fee is non-refundable.",
  "I consent to electronic records and digital signatures as legally binding.",
];

const VISA_RENEWAL_DECLARATION = "In case of visa renewal via airport or bus, I agree to bear the full deportation ticket cost if the visa is rejected or denied.";

// Visa types that require the renewal declaration
const RENEWAL_VISA_TYPES = ["airport_to_airport", "visa_change_bus"];

const GuaranteeForm = () => {
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Visa types from API
  const [visaTypes, setVisaTypes] = useState([]);
  const [renewalVisaTypes, setRenewalVisaTypes] = useState([]);

  // Multiple passengers support
  const emptyPassenger = {
    full_name: "",
    passport_number: "",
    nationality: "",
    date_of_birth: "",
    contact_number: "",
    email: "",
    relation_to_guarantor: "",
    other_relation: "",  // Used when relation_to_guarantor is "other"
  };

  // Relation options for applicants
  const RELATION_OPTIONS = [
    { value: "self", label: "Self" },
    { value: "spouse", label: "Spouse" },
    { value: "parent", label: "Parent" },
    { value: "child", label: "Child" },
    { value: "sibling", label: "Sibling" },
    { value: "friend", label: "Friend" },
    { value: "relative", label: "Relative" },
    { value: "employer", label: "Employer" },
    { value: "other", label: "Other" },
  ];

  // Form state - now supports multiple passengers
  const [passengers, setPassengers] = useState([{ ...emptyPassenger }]);
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(0);
  
  // Current passenger for easy access
  const passenger = passengers[currentPassengerIndex] || emptyPassenger;
  
  const setPassenger = (newPassenger) => {
    const updated = [...passengers];
    updated[currentPassengerIndex] = typeof newPassenger === 'function' 
      ? newPassenger(passengers[currentPassengerIndex])
      : newPassenger;
    setPassengers(updated);
  };
  
  const addPassenger = () => {
    setPassengers([...passengers, { ...emptyPassenger }]);
    setPassportImages([...passportImages, null]);
    setCurrentPassengerIndex(passengers.length);
    setOcrFields((prev) => ({ ...prev, passenger: [] })); // Clear OCR highlights
  };
  
  const removePassenger = (index) => {
    if (passengers.length === 1) return;
    const updated = passengers.filter((_, i) => i !== index);
    const updatedImages = passportImages.filter((_, i) => i !== index);
    setPassengers(updated);
    setPassportImages(updatedImages);
    if (currentPassengerIndex >= updated.length) {
      setCurrentPassengerIndex(updated.length - 1);
    }
  };
  
  // Clear OCR highlights when switching applicants
  const handleSwitchApplicant = (index) => {
    setCurrentPassengerIndex(index);
    setOcrFields((prev) => ({ ...prev, passenger: [] }));
  };

  const [guarantor, setGuarantor] = useState({
    guarantor_type: "",
    other_description: "",
    full_name: "",
    id_type: "emirates_id",
    id_number: "",
    nationality: "",
    contact_number: "",
    email: "",
    relationship_to_passenger: "",
  });

  const [visaType, setVisaType] = useState("");
  const [declarations, setDeclarations] = useState([]);
  const [passportImages, setPassportImages] = useState([null]); // Array for multiple passengers
  const [guarantorIdImage, setGuarantorIdImage] = useState(null);
  const [guarantorIdBackImage, setGuarantorIdBackImage] = useState(null);
  const [ocrFields, setOcrFields] = useState({ passenger: [], guarantor: [] });
  
  // Get/set passport image for current passenger
  const passportImage = passportImages[currentPassengerIndex] || null;
  const setPassportImage = (image) => {
    const updated = [...passportImages];
    updated[currentPassengerIndex] = image;
    setPassportImages(updated);
  };

  // Default visa types fallback
  const DEFAULT_VISA_TYPES = [
    { value: "30_days_single", label: "30 Days Single Entry", is_renewal_type: false },
    { value: "60_days_single", label: "60 Days Single Entry", is_renewal_type: false },
    { value: "30_days_multiple", label: "30 Days Multiple Entry", is_renewal_type: false },
    { value: "60_days_multiple", label: "60 Days Multiple Entry", is_renewal_type: false },
    { value: "48_hours_transit", label: "48 Hours Transit Visa", is_renewal_type: false },
    { value: "96_hours_transit", label: "96 Hours Transit Visa", is_renewal_type: false },
    { value: "airport_to_airport", label: "Airport-to-Airport Visa Change", is_renewal_type: true },
    { value: "visa_change_bus", label: "Visa Change by Bus", is_renewal_type: true },
  ];

  // Fetch visa types on mount
  useEffect(() => {
    const fetchVisaTypes = async () => {
      try {
        const response = await axios.get(`${API}/visa-types`);
        if (response.data && response.data.length > 0) {
          setVisaTypes(response.data);
          setRenewalVisaTypes(response.data.filter(vt => vt.is_renewal_type).map(vt => vt.value));
        } else {
          setVisaTypes(DEFAULT_VISA_TYPES);
          setRenewalVisaTypes(DEFAULT_VISA_TYPES.filter(vt => vt.is_renewal_type).map(vt => vt.value));
        }
      } catch (error) {
        console.error("Error fetching visa types:", error);
        // Fallback to default
        setVisaTypes(DEFAULT_VISA_TYPES);
        setRenewalVisaTypes(DEFAULT_VISA_TYPES.filter(vt => vt.is_renewal_type).map(vt => vt.value));
      }
    };
    fetchVisaTypes();
  }, []);

  // Email validation
  const isValidEmail = (email) => EMAIL_REGEX.test(email);

  // Auto-fill guarantor when an applicant selects "self" as relation
  useEffect(() => {
    const selfApplicant = passengers.find(p => p.relation_to_guarantor === "self");
    if (selfApplicant) {
      setGuarantor(prev => ({
        ...prev,
        guarantor_type: "self",
        id_type: "passport",
        full_name: selfApplicant.full_name,
        id_number: selfApplicant.passport_number,
        nationality: selfApplicant.nationality,
        contact_number: selfApplicant.contact_number,
        email: selfApplicant.email,
      }));
    } else if (guarantor.guarantor_type === "self") {
      // Reset if no self relation anymore
      setGuarantor(prev => ({
        ...prev,
        guarantor_type: "other",
        id_type: "emirates_id",
        full_name: "",
        id_number: "",
        nationality: "",
        contact_number: "",
        email: "",
      }));
    }
  }, [passengers]);

  // Check if any applicant is self-guarantor
  const isSelfGuarantor = passengers.some(p => p.relation_to_guarantor === "self");

  // Get declarations based on visa type
  const getDeclarations = () => {
    if (renewalVisaTypes.includes(visaType)) {
      return [...BASE_DECLARATIONS.slice(0, 2), VISA_RENEWAL_DECLARATION, ...BASE_DECLARATIONS.slice(2)];
    }
    return BASE_DECLARATIONS;
  };

  const currentDeclarations = getDeclarations();

  const steps = [
    { number: 1, label: "Applicant" },
    { number: 2, label: "Guarantor" },
    { number: 3, label: "Visa Type" },
    { number: 4, label: "Declarations" },
    { number: 5, label: "Signature" },
  ];

  // Handle file upload and OCR
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(",")[1];
      
      if (type === "passport") {
        setPassportImage(reader.result);
        await extractOCR(base64, "passport", "passenger");
      } else {
        setGuarantorIdImage(reader.result);
        await extractOCR(base64, guarantor.id_type, "guarantor");
      }
    };
    reader.readAsDataURL(file);
  };

  const extractOCR = async (base64, docType, target) => {
    setOcrLoading(true);
    try {
      const response = await axios.post(`${API}/ocr/extract`, {
        image_base64: base64,
        document_type: docType,
      });

      if (response.data.success) {
        if (target === "passenger") {
          const newFields = [];
          // Update current passenger in the array directly
          const updatedPassengers = [...passengers];
          const currentPassenger = { ...updatedPassengers[currentPassengerIndex] };
          
          if (response.data.full_name) {
            currentPassenger.full_name = response.data.full_name;
            newFields.push("full_name");
          }
          if (response.data.id_number) {
            currentPassenger.passport_number = response.data.id_number;
            newFields.push("passport_number");
          }
          if (response.data.nationality) {
            currentPassenger.nationality = response.data.nationality;
            newFields.push("nationality");
          }
          if (response.data.date_of_birth) {
            currentPassenger.date_of_birth = response.data.date_of_birth;
            newFields.push("date_of_birth");
          }
          
          updatedPassengers[currentPassengerIndex] = currentPassenger;
          setPassengers(updatedPassengers);
          setOcrFields((prev) => ({ ...prev, passenger: newFields }));
          toast.success("Passport data extracted successfully");
        } else {
          // Guarantor OCR
          const newFields = [];
          if (response.data.full_name) {
            setGuarantor((prev) => ({ ...prev, full_name: response.data.full_name }));
            newFields.push("full_name");
          }
          if (response.data.id_number) {
            setGuarantor((prev) => ({ ...prev, id_number: response.data.id_number }));
            newFields.push("id_number");
          }
          if (response.data.nationality) {
            setGuarantor((prev) => ({ ...prev, nationality: response.data.nationality }));
            newFields.push("nationality");
          }
          setOcrFields((prev) => ({ ...prev, guarantor: newFields }));
          toast.success("Guarantor document data extracted successfully");
        }
      } else {
        toast.error(response.data.message || "OCR extraction failed");
      }
    } catch (error) {
      console.error("OCR error:", error);
      toast.error("Failed to extract document data");
    } finally {
      setOcrLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Please provide your signature");
      return;
    }

    if (declarations.length !== currentDeclarations.length || !declarations.every((d) => d)) {
      toast.error("Please accept all declarations");
      return;
    }
    
    // Validate all passengers have required fields
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.full_name || !p.passport_number || !p.nationality || !p.contact_number || !p.email) {
        toast.error(`Please complete all required fields for Applicant ${i + 1}`);
        setCurrentPassengerIndex(i);
        setCurrentStep(1);
        return;
      }
    }

    setLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      
      // Normalize declarations to always send 10 items for backend compatibility
      const normalizedDeclarations = new Array(10).fill(true);
      
      const payload = {
        passengers: passengers, // Send all passengers
        guarantor: {
          ...guarantor,
          relationship_to_passenger: guarantor.guarantor_type === "self" 
            ? "Self" 
            : guarantor.relationship_to_passenger || guarantor.guarantor_type,
        },
        visa_type: visaTypes.find((v) => v.value === visaType)?.label || visaType,
        declarations: normalizedDeclarations,
        signature_data: signatureData,
        passport_images: passportImages,
        guarantor_id_image: guarantorIdImage,
        guarantor_id_back_image: guarantorIdBackImage,
      };

      const response = await axios.post(`${API}/guarantee-letter`, payload);
      toast.success("Guarantee letter created successfully!");
      // Small delay to ensure toast is visible
      setTimeout(() => {
        navigate(`/success/${response.data.id}`);
      }, 500);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to create guarantee letter");
    } finally {
      setLoading(false);
    }
  };

  const handleDeclarationChange = (index) => {
    const newDeclarations = [...declarations];
    newDeclarations[index] = !newDeclarations[index];
    setDeclarations(newDeclarations);
  };

  const selectAllDeclarations = () => {
    setDeclarations(new Array(currentDeclarations.length).fill(true));
  };

  // Reset declarations when visa type changes
  const handleVisaTypeChange = (type) => {
    setVisaType(type);
    // Reset declarations when visa type changes as the list might be different
    const newDeclarationCount = renewalVisaTypes.includes(type) 
      ? BASE_DECLARATIONS.length + 1 
      : BASE_DECLARATIONS.length;
    setDeclarations(new Array(newDeclarationCount).fill(false));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        const relationValid = passenger.relation_to_guarantor && 
          (passenger.relation_to_guarantor !== "other" || passenger.other_relation);
        return passenger.full_name && 
               passenger.passport_number && 
               passenger.nationality && 
               passenger.contact_number && 
               passenger.email && 
               isValidEmail(passenger.email) &&
               relationValid;
      case 2:
        // For self-guarantor, details come from applicant
        if (isSelfGuarantor) {
          return guarantor.full_name && guarantor.email;
        }
        // For others with Emirates ID, require both front and back images
        if (guarantor.id_type === "emirates_id") {
          const requiresBackImage = guarantorIdImage && guarantorIdBackImage && guarantorIdImage !== guarantorIdBackImage;
          return guarantor.full_name && 
                 guarantor.id_number && 
                 guarantor.nationality && 
                 guarantor.contact_number && 
                 guarantor.email && 
                 isValidEmail(guarantor.email) &&
                 requiresBackImage;
        }
        return guarantor.full_name && 
               guarantor.id_number && 
               guarantor.nationality && 
               guarantor.contact_number && 
               guarantor.email && 
               isValidEmail(guarantor.email);
      case 3:
        return visaType;
      case 4:
        return declarations.length === currentDeclarations.length && declarations.every((d) => d);
      case 5:
        return true;
      default:
        return false;
    }
  };

  const formData = {
    passengers,
    currentPassengerIndex,
    passenger,
    guarantor,
    visaType: visaTypes.find((v) => v.value === visaType)?.label || visaType,
    declarations,
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900">Guarantee Letter Application</h1>
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden"
            onClick={() => setShowPreview(true)}
            data-testid="mobile-preview-btn"
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
        </div>
      </header>

      <div className="flex-1 lg:grid lg:grid-cols-2">
        {/* Form Panel */}
        <div className="form-panel bg-white">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <button
                  onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                  className={`step-indicator ${
                    currentStep === step.number
                      ? "active"
                      : currentStep > step.number
                      ? "completed"
                      : "pending"
                  }`}
                  disabled={step.number > currentStep}
                  data-testid={`step-${step.number}-btn`}
                >
                  {currentStep > step.number ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    step.number
                  )}
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 sm:w-16 h-0.5 mx-1 ${
                      currentStep > step.number ? "bg-emerald-500" : "bg-slate-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="form-section">
            {/* Step 1: Passenger Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                      Applicant Details {passengers.length > 1 && `(${currentPassengerIndex + 1}/${passengers.length})`}
                    </h2>
                    <p className="text-slate-600">Upload passport for automatic data extraction</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addPassenger}
                    data-testid="add-applicant-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Applicant
                  </Button>
                </div>
                
                {/* Applicant tabs if multiple */}
                {passengers.length > 1 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-sm border border-slate-200">
                    {passengers.map((p, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <button
                          onClick={() => handleSwitchApplicant(index)}
                          className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                            currentPassengerIndex === index
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                          }`}
                          data-testid={`applicant-tab-${index}`}
                        >
                          {p.full_name || `Applicant ${index + 1}`}
                        </button>
                        {passengers.length > 1 && (
                          <button
                            onClick={() => removePassenger(index)}
                            className="p-1 text-slate-400 hover:text-red-500"
                            data-testid={`remove-applicant-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Passport Upload */}
                <div className="upload-zone relative" onClick={() => document.getElementById("passport-upload").click()}>
                  <input
                    type="file"
                    id="passport-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, "passport")}
                    data-testid="passport-upload-input"
                  />
                  {passportImage ? (
                    <div className="flex items-center gap-4">
                      <img src={passportImage} alt="Passport" className="w-20 h-14 object-cover rounded" />
                      <div className="text-left">
                        <p className="font-medium text-slate-900">Passport uploaded</p>
                        <p className="text-sm text-slate-500">Click to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div className="py-4">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="font-medium text-slate-700">Upload Passport Copy</p>
                      <p className="text-sm text-slate-500">Click or drag & drop</p>
                    </div>
                  )}
                  {ocrLoading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-900" />
                      <span className="ml-2 text-sm">Extracting data...</span>
                    </div>
                  )}
                </div>

                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="passenger-name">Full Name (as per passport) *</Label>
                    <Input
                      id="passenger-name"
                      value={passenger.full_name}
                      onChange={(e) => setPassenger({ ...passenger, full_name: e.target.value })}
                      className={ocrFields.passenger.includes("full_name") ? "ocr-highlight" : ""}
                      data-testid="passenger-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="passport-number">Passport Number *</Label>
                    <Input
                      id="passport-number"
                      value={passenger.passport_number}
                      onChange={(e) => setPassenger({ ...passenger, passport_number: e.target.value })}
                      className={ocrFields.passenger.includes("passport_number") ? "ocr-highlight" : ""}
                      data-testid="passport-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nationality">Nationality *</Label>
                    <Input
                      id="nationality"
                      value={passenger.nationality}
                      onChange={(e) => setPassenger({ ...passenger, nationality: e.target.value })}
                      className={ocrFields.passenger.includes("nationality") ? "ocr-highlight" : ""}
                      data-testid="passenger-nationality-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={passenger.date_of_birth}
                      onChange={(e) => setPassenger({ ...passenger, date_of_birth: e.target.value })}
                      className={ocrFields.passenger.includes("date_of_birth") ? "ocr-highlight" : ""}
                      data-testid="passenger-dob-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact">Contact Number *</Label>
                    <Input
                      id="contact"
                      value={passenger.contact_number}
                      onChange={(e) => setPassenger({ ...passenger, contact_number: e.target.value })}
                      data-testid="passenger-contact-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={passenger.email}
                      onChange={(e) => setPassenger({ ...passenger, email: e.target.value })}
                      className={passenger.email && !isValidEmail(passenger.email) ? "border-red-300" : ""}
                      data-testid="passenger-email-input"
                    />
                    {passenger.email && !isValidEmail(passenger.email) && (
                      <p className="text-sm text-red-500 mt-1">Please enter a valid email address</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="relation">Relation to Guarantor *</Label>
                    <Select
                      value={passenger.relation_to_guarantor}
                      onValueChange={(value) => setPassenger({ ...passenger, relation_to_guarantor: value, other_relation: value !== "other" ? "" : passenger.other_relation })}
                    >
                      <SelectTrigger data-testid="relation-to-guarantor-select">
                        <SelectValue placeholder="Select relation" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {passenger.relation_to_guarantor === "other" && (
                    <div>
                      <Label htmlFor="other-relation">Please specify relation *</Label>
                      <Input
                        id="other-relation"
                        value={passenger.other_relation}
                        onChange={(e) => setPassenger({ ...passenger, other_relation: e.target.value })}
                        placeholder="Enter relation"
                        data-testid="other-relation-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Guarantor Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Guarantor Details</h2>
                  <p className="text-slate-600">Enter details of the person guaranteeing the visa application</p>
                </div>

                {/* Check if any applicant is self-guarantor */}
                {isSelfGuarantor ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
                    <p className="text-emerald-800 font-medium">Self Guarantor</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      The applicant is their own guarantor. Details will be taken from applicant information.
                    </p>
                  </div>
                ) : null}

                <div className="grid gap-4">
                  {!isSelfGuarantor && (
                    <>
                      <div>
                        <Label>ID Type</Label>
                        <Select
                          value={guarantor.id_type}
                          onValueChange={(value) => setGuarantor({ ...guarantor, id_type: value })}
                        >
                          <SelectTrigger data-testid="guarantor-id-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="emirates_id">Emirates ID (UAE Resident)</SelectItem>
                            <SelectItem value="passport">Passport</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Emirates ID Upload - Front */}
                      {guarantor.id_type === "emirates_id" && (
                        <div className="space-y-3">
                          <div className="upload-zone relative" onClick={() => document.getElementById("guarantor-id-front-upload").click()}>
                            <input
                              type="file"
                              id="guarantor-id-front-upload"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileUpload(e, "guarantor")}
                              data-testid="guarantor-id-front-upload-input"
                            />
                            {guarantorIdImage ? (
                              <div className="flex items-center gap-4">
                                <img src={guarantorIdImage} alt="Emirates ID Front" className="w-20 h-14 object-cover rounded" />
                                <div className="text-left">
                                  <p className="font-medium text-slate-900">Emirates ID Front uploaded</p>
                                  <p className="text-sm text-slate-500">Click to replace</p>
                                </div>
                              </div>
                            ) : (
                              <div className="py-4">
                                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                                <p className="font-medium text-slate-700">Upload Emirates ID (Front)</p>
                                <p className="text-sm text-slate-500">Click or drag & drop</p>
                              </div>
                            )}
                            {ocrLoading && (
                              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 animate-spin text-slate-900" />
                                <span className="ml-2 text-sm">Extracting data...</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Emirates ID Back - No OCR */}
                          <div className="upload-zone relative" onClick={() => document.getElementById("guarantor-id-back-upload").click()}>
                            <input
                              type="file"
                              id="guarantor-id-back-upload"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => setGuarantorIdBackImage(reader.result);
                                  reader.readAsDataURL(file);
                                }
                              }}
                              data-testid="guarantor-id-back-upload-input"
                            />
                            {guarantorIdBackImage ? (
                              <div className="flex items-center gap-4">
                                <img src={guarantorIdBackImage} alt="Emirates ID Back" className="w-20 h-14 object-cover rounded" />
                                <div className="text-left">
                                  <p className="font-medium text-slate-900">Emirates ID Back uploaded</p>
                                  <p className="text-sm text-slate-500">Click to replace</p>
                                </div>
                              </div>
                            ) : (
                              <div className="py-4">
                                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                                <p className="font-medium text-slate-700">Upload Emirates ID (Back) *</p>
                                <p className="text-sm text-slate-500">Click or drag & drop</p>
                              </div>
                            )}
                          </div>
                          {guarantorIdImage && guarantorIdBackImage && guarantorIdImage === guarantorIdBackImage && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-sm">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                              <span className="text-sm text-red-700">
                                Front and back images must be different. Please upload the back side of Emirates ID.
                              </span>
                            </div>
                          )}
                          {guarantor.id_type === "emirates_id" && (!guarantorIdImage || !guarantorIdBackImage) && (
                            <p className="text-sm text-amber-600">Both front and back images of Emirates ID are required</p>
                          )}
                        </div>
                      )}

                      {/* Passport Upload */}
                      {guarantor.id_type === "passport" && (
                        <div className="upload-zone relative" onClick={() => document.getElementById("guarantor-id-upload").click()}>
                          <input
                            type="file"
                            id="guarantor-id-upload"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, "guarantor")}
                            data-testid="guarantor-id-upload-input"
                          />
                          {guarantorIdImage ? (
                            <div className="flex items-center gap-4">
                              <img src={guarantorIdImage} alt="Passport" className="w-20 h-14 object-cover rounded" />
                              <div className="text-left">
                                <p className="font-medium text-slate-900">Passport uploaded</p>
                                <p className="text-sm text-slate-500">Click to replace</p>
                              </div>
                            </div>
                          ) : (
                            <div className="py-4">
                              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                              <p className="font-medium text-slate-700">Upload Guarantor Passport</p>
                              <p className="text-sm text-slate-500">Click or drag & drop</p>
                            </div>
                          )}
                          {ocrLoading && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                              <Loader2 className="w-6 h-6 animate-spin text-slate-900" />
                              <span className="ml-2 text-sm">Extracting data...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <Label htmlFor="guarantor-name">Full Name *</Label>
                    <Input
                      id="guarantor-name"
                      value={guarantor.full_name}
                      onChange={(e) => setGuarantor({ ...guarantor, full_name: e.target.value })}
                      disabled={isSelfGuarantor}
                      className={ocrFields.guarantor.includes("full_name") ? "ocr-highlight" : ""}
                      data-testid="guarantor-name-input"
                    />
                  </div>

                  {!isSelfGuarantor && (
                    <div>
                      <Label htmlFor="guarantor-id">
                        {guarantor.id_type === "emirates_id" ? "Emirates ID Number" : "Passport Number"} *
                      </Label>
                      <Input
                        id="guarantor-id"
                        value={guarantor.id_number}
                        onChange={(e) => setGuarantor({ ...guarantor, id_number: e.target.value })}
                        className={ocrFields.guarantor.includes("id_number") ? "ocr-highlight" : ""}
                        data-testid="guarantor-id-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="guarantor-nationality">Nationality *</Label>
                    <Input
                      id="guarantor-nationality"
                      value={guarantor.nationality}
                      onChange={(e) => setGuarantor({ ...guarantor, nationality: e.target.value })}
                      disabled={isSelfGuarantor}
                      className={ocrFields.guarantor.includes("nationality") ? "ocr-highlight" : ""}
                      data-testid="guarantor-nationality-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guarantor-contact">Contact Number *</Label>
                    <Input
                      id="guarantor-contact"
                      value={guarantor.contact_number}
                      onChange={(e) => setGuarantor({ ...guarantor, contact_number: e.target.value })}
                      disabled={isSelfGuarantor}
                      data-testid="guarantor-contact-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guarantor-email">Email Address *</Label>
                    <Input
                      id="guarantor-email"
                      type="email"
                      value={guarantor.email}
                      onChange={(e) => setGuarantor({ ...guarantor, email: e.target.value })}
                      disabled={isSelfGuarantor}
                      className={guarantor.email && !isValidEmail(guarantor.email) ? "border-red-300" : ""}
                      data-testid="guarantor-email-input"
                    />
                    {guarantor.email && !isValidEmail(guarantor.email) && (
                      <p className="text-sm text-red-500 mt-1">Please enter a valid email address</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Visa Type */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Visa Type</h2>
                  <p className="text-slate-600">Choose the visa type for this application</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  {visaTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => handleVisaTypeChange(type.value)}
                      className={`visa-type-card text-left ${visaType === type.value ? "selected" : ""}`}
                      data-testid={`visa-type-${type.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{type.label}</span>
                        {visaType === type.value && (
                          <Check className="w-4 h-4 text-slate-900" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Declarations */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Declarations</h2>
                    <p className="text-slate-600">Please read and accept all declarations</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllDeclarations}
                    data-testid="select-all-declarations-btn"
                  >
                    Select All
                  </Button>
                </div>

                <div className="space-y-3">
                  {currentDeclarations.map((declaration, index) => (
                    <div
                      key={index}
                      className={`declaration-item cursor-pointer ${declarations[index] ? "checked" : ""}`}
                      onClick={() => handleDeclarationChange(index)}
                    >
                      <Checkbox
                        checked={declarations[index] || false}
                        onCheckedChange={() => handleDeclarationChange(index)}
                        data-testid={`declaration-${index}`}
                      />
                      <span className="text-sm text-slate-700 leading-relaxed">{declaration}</span>
                    </div>
                  ))}
                </div>
                
                {/* Liability Statement */}
                <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-sm">
                  <h3 className="font-semibold text-slate-900 mb-3">LIABILITY & INDEMNITY AGREEMENT</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    The undersigned acknowledges and agrees that all liability for any immigration-related penalties, fines, overstay charges, absconding fees, visa auto-renewal charges, or other legal consequences shall rest solely with the applicant and/or guarantor. In the event that any such charges arise, the applicant and/or guarantor shall be fully responsible and legally bound to pay the applicable amount to White Sky Travel and Tourism LLC.
                  </p>
                </div>
                
                {/* Definitions */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">DEFINITIONS</h4>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>
                      <strong>Applicant (Visa Applicant):</strong> The individual whose passport details are provided and who is applying for the UAE visa and will travel to the UAE.
                    </p>
                    <p>
                      <strong>Guarantor:</strong> The individual or entity providing financial and legal responsibility for the Applicant.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Signature */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Digital Signature</h2>
                  <p className="text-slate-600">Sign below to confirm your acceptance</p>
                </div>

                <div className="signature-container">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: "signature-canvas w-full",
                      style: { height: "200px", width: "100%" },
                    }}
                    data-testid="signature-canvas"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={clearSignature}
                    className="flex-1"
                    data-testid="clear-signature-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                  <p className="text-sm text-slate-600">
                    <strong>Signatory:</strong> {guarantor.full_name || "Not specified"}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {currentStep < 5 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !declarations.every((d) => d)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="submit-form-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <Check className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="preview-panel">
          <div className="sticky top-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Live Preview</h3>
            <PDFPreview data={formData} />
          </div>
        </div>
      </div>

      {/* Mobile Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setShowPreview(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Document Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <PDFPreview data={formData} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GuaranteeForm;
